/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * "Was dit artikel nuttig?" — ja/nee vote at the end of every kennisbank
 * article. Fires article_helpful_yes / article_helpful_no through the full
 * analytics pipeline (GA4 + first-party /api/event-ping). Two event
 * NAMES on purpose: the first-party sink keeps name-only counters, so the
 * yes/no split shows in the admin Events panel with zero server work; the
 * per-article split stays recoverable from the event log's path field.
 * One vote per article per browser (localStorage, articleHelpfulMemory.ts).
 */

import React, { useEffect, useState } from 'react';
import { CheckCircle2, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { trackEvent, SURFACES } from '../lib/gtag';
import {
  getStoredHelpfulVote,
  storeHelpfulVote,
  type HelpfulVote,
} from '../lib/articleHelpfulMemory';

interface ArticleHelpfulWidgetProps {
  articleId: string;
  articleSlug: string;
}

const VOTE_BUTTON_CLASS =
  'inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-black border border-slate-700 ' +
  'bg-slate-800/80 hover:bg-slate-700/80 hover:border-emerald-500/40 text-slate-200 transition-all';

export const ArticleHelpfulWidget: React.FC<ArticleHelpfulWidgetProps> = ({
  articleId,
  articleSlug,
}) => {
  const { t, language } = useLanguage();
  const [vote, setVote] = useState<HelpfulVote | null>(null);

  // Read the stored vote in an effect, NOT a lazy initializer: the page is
  // prerendered with the question visible, so the first client render must
  // match that HTML (hydration parity). Returning voters see the thank-you
  // swap one paint later.
  useEffect(() => {
    setVote(getStoredHelpfulVote(articleId));
  }, [articleId]);

  const onVote = (v: HelpfulVote) => {
    if (vote) return;
    storeHelpfulVote(articleId, v);
    setVote(v);
    trackEvent(v === 'yes' ? 'article_helpful_yes' : 'article_helpful_no', {
      surface: SURFACES.article,
      language,
      article_slug: articleSlug,
    });
  };

  return (
    <div className="mt-16 bg-slate-900/40 border border-slate-800 rounded-3xl p-6 md:p-8 text-center">
      <div aria-live="polite">
        {vote ? (
          <p className="inline-flex items-center gap-2 text-white font-black">
            <CheckCircle2 aria-hidden="true" className="w-5 h-5 text-emerald-400" />
            {t('kennisbank.helpful_thanks')}
          </p>
        ) : (
          <>
            <p className="text-white font-black text-lg mb-5">{t('kennisbank.helpful_question')}</p>
            <div className="flex items-center justify-center gap-3">
              <button type="button" onClick={() => onVote('yes')} className={VOTE_BUTTON_CLASS}>
                <ThumbsUp aria-hidden="true" className="w-4 h-4 text-emerald-400" />
                {t('kennisbank.helpful_yes')}
              </button>
              <button type="button" onClick={() => onVote('no')} className={VOTE_BUTTON_CLASS}>
                <ThumbsDown aria-hidden="true" className="w-4 h-4 text-slate-400" />
                {t('kennisbank.helpful_no')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
